import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { getUserProfilePictureUrl } from '../../../../common/userHelper';
import colorTokens from '../../../../constants/design_system/colors';

export interface MentionListProps {
  items: Array<{ id: number; display: string; profilePicturePath?: string }>;
  command: (item: { id: number; label: string }) => void;
  users: Array<{ id: number; display: string; profilePicturePath?: string }>;
  onExit?: () => void;
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      setSelectedIndex(index);
      props.command({ id: item.id, label: item.display });
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    if (props.items.length === 0) {
      // If no results, exit mention search mode
      props.onExit?.();
      return;
    }
    // If there are results, select the current item
    selectItem(selectedIndex);
  };

  const escapeHandler = () => {
    // Always exit mention search mode on ESC
    props.onExit?.();
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current && props.items.length > 0) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex, props.items.length]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        // Only handle arrows if there are results
        if (props.items.length > 0) {
          upHandler();
        }
        return true;
      }

      if (event.key === 'ArrowDown') {
        // Only handle arrows if there are results
        if (props.items.length > 0) {
          downHandler();
        }
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      if (event.key === 'Escape') {
        escapeHandler();
        return true;
      }

      // Space key should keep mention search active (don't handle it)
      if (event.key === ' ') {
        return false;
      }

      return false;
    },
  }));

  if (props.items.length === 0) {
    return (
      <div className="bg-white rounded-md shadow-lg border border-neutral-200 p-3 text-sm text-neutral-500">
        No results
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="bg-white rounded-md shadow-lg border border-neutral-200 py-2 max-h-60 overflow-y-auto"
    >
      <div className="px-3 py-2 text-xs font-semibold text-neutral-900 border-b border-neutral-100">
        Choose a member to mention
      </div>
      {props.items.map((item, index) => (
        <div
          key={item.id}
          ref={index === selectedIndex ? selectedItemRef : null}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors cursor-pointer ${
            index === selectedIndex ? 'bg-blue-100 hover:bg-blue-200' : 'hover:bg-neutral-200'
          }`}
          // Keep the DOM selection in the editor. Safari otherwise moves it here, and
          // Tiptap's collapseToEnd() after the insert throws InvalidStateError.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => selectItem(index)}
        >
          <Avatar
            size={20}
            icon={<UserOutlined />}
            style={{ backgroundColor: colorTokens.neutral[400] }}
            src={getUserProfilePictureUrl(item.profilePicturePath || null)}
          />
          <span className="text-sm font-normal text-neutral-900">{item.display}</span>
        </div>
      ))}
    </div>
  );
});

MentionList.displayName = 'MentionList';
